/**
 * Dynamic Progress Calculator
 *
 * Calculates progress based on core questions identified by core-if conditions.
 * Core questions receive equal step distribution (100 / N each), and non-core
 * questions between two core anchors receive simulated progress proportional
 * to how many have been visited.
 *
 * The set of active core questions is re-evaluated on every call against the
 * current formResults, so flow changes (quick <-> deep-dive) and conditional
 * core questions are reflected immediately.
 */

import type { FormStepResult } from '../types'
import { coreQuestionEvaluator } from './coreQuestionEvaluator'
import type { CoreIfCondition } from '../../types/form'

export interface QuestionStepWithCoreIf {
  name: string
  coreIf?: CoreIfCondition
}

export interface ProgressCalculationOptions {
  mode: 'step' | 'percentage'
  currentStepIndex: number
  questions: QuestionStepWithCoreIf[]
  formResults: FormStepResult
}

export interface ProgressCalculationResult {
  progressPercentage: number
  coreQuestionsCount: number
  completedCoreQuestions: number
  currentCoreQuestion?: QuestionStepWithCoreIf
  nextCoreQuestion?: QuestionStepWithCoreIf
  questionsSinceCurrentCore: number
  totalQuestionsUntilNextCore: number
  currentStepNumber?: number
  totalSteps?: number
}

type CoreQuestionEvaluator = ReturnType<typeof coreQuestionEvaluator>

interface CorePositionAnalysis {
  completedCoreCount: number
  currentCoreQuestion?: QuestionStepWithCoreIf
  nextCoreQuestion?: QuestionStepWithCoreIf
  questionsSinceCurrentCore: number
  totalQuestionsUntilNextCore: number
}

const EMPTY_RESULT: ProgressCalculationResult = {
  progressPercentage: 0,
  coreQuestionsCount: 0,
  completedCoreQuestions: 0,
  questionsSinceCurrentCore: 0,
  totalQuestionsUntilNextCore: 0,
}

/**
 * Progress is intentionally capped below 100% so the bar never appears
 * "complete" before the user has actually submitted the form.
 */
const MAX_PRE_COMPLETION_PROGRESS = 95

/**
 * Dynamic progress calculator that adapts to changing question sets and core status.
 *
 * Key behaviours:
 * - Equal step distribution across all active core questions
 * - Simulation increment for non-core questions (percentage mode only)
 * - step mode skips interpolation - use for Quick flow "X/Y" display
 */
export class dynamicProgressCalculator {
  calculate(options: ProgressCalculationOptions): ProgressCalculationResult {
    const { mode, currentStepIndex, questions, formResults } = options
    const evaluator = coreQuestionEvaluator(formResults)

    const coreQuestions = questions.filter((question) => evaluator.isCoreQuestion(question.coreIf))

    if (coreQuestions.length === 0) return EMPTY_RESULT

    const stepSize = 100 / coreQuestions.length
    const coreAnalysis = this.analyzeCorePosition(questions, currentStepIndex, evaluator)

    const isOnNonCoreStep = !coreAnalysis.currentCoreQuestion

    // Core step (step mode): advance fill to include the current core step being entered.
    // Core step (percentage mode): base at the completed-cores milestone; simulation adds
    //   interpolation for non-core questions between cores.
    // Non-core step (percentage mode): base one milestone behind so simulation can fill the gap.
    // Non-core step (step mode): hold at the last completed core's milestone - no fill change
    //   until the next core step is reached.
    const baseProgress = (() => {
      if (!isOnNonCoreStep && mode === 'step') {
        // Arriving at a core step in quick flow: count it immediately so the fill
        // advances in lock-step with the "X/Y" counter the user sees.
        return (coreAnalysis.completedCoreCount + 1) * stepSize
      }
      if (isOnNonCoreStep && mode === 'percentage') {
        return Math.max(0, (coreAnalysis.completedCoreCount - 1) * stepSize)
      }
      return coreAnalysis.completedCoreCount * stepSize
    })()

    // When there is no next core (e.g. the last question in the flow is non-core),
    // treat the form's implicit end as the next milestone so the zone interpolation
    // still produces a visible increment above the previous core's value.
    const simulationZoneSize =
      coreAnalysis.totalQuestionsUntilNextCore > 0
        ? coreAnalysis.totalQuestionsUntilNextCore
        : coreAnalysis.questionsSinceCurrentCore

    const simulationProgress =
      isOnNonCoreStep && mode === 'percentage' && simulationZoneSize > 0
        ? (stepSize / (simulationZoneSize + 1)) * coreAnalysis.questionsSinceCurrentCore
        : 0

    const finalProgress = Math.min(baseProgress + simulationProgress, MAX_PRE_COMPLETION_PROGRESS)

    return {
      progressPercentage: Math.round(finalProgress),
      coreQuestionsCount: coreQuestions.length,
      completedCoreQuestions: coreAnalysis.completedCoreCount,
      currentCoreQuestion: coreAnalysis.currentCoreQuestion,
      nextCoreQuestion: coreAnalysis.nextCoreQuestion,
      questionsSinceCurrentCore: coreAnalysis.questionsSinceCurrentCore,
      totalQuestionsUntilNextCore: coreAnalysis.totalQuestionsUntilNextCore,
      // On a core step: user is "starting" that step, so count = completed + 1.
      // On a non-core step: user is still within the previous core's zone, so count = completed.
      // e.g. homeInfo (non-core) after addressAndMover (core 2 of 4) -> shows "2/4".
      currentStepNumber: coreAnalysis.currentCoreQuestion
        ? coreAnalysis.completedCoreCount + 1
        : coreAnalysis.completedCoreCount,
      totalSteps: coreQuestions.length,
    }
  }

  private analyzeCorePosition(
    allQuestions: QuestionStepWithCoreIf[],
    currentStepIndex: number,
    evaluator: CoreQuestionEvaluator,
  ): CorePositionAnalysis {
    let completedCoreCount = 0
    let currentCoreQuestion: QuestionStepWithCoreIf | undefined
    let nextCoreQuestion: QuestionStepWithCoreIf | undefined
    let questionsSinceCurrentCore = 0
    let totalQuestionsUntilNextCore = 0

    let foundCurrentCore = false
    let foundNextCore = false

    // 1-based count of non-core steps since the last completed core. Resets whenever a
    // completed core is encountered. Captures the current step's zone position when the
    // current step is itself non-core (foundCurrentCore never becomes true in that case).
    let nonCoreZonePosition = 0

    for (let questionIndex = 0; questionIndex < allQuestions.length; questionIndex++) {
      const question = allQuestions[questionIndex]
      if (!question) continue

      const isCore = evaluator.isCoreQuestion(question.coreIf)

      if (isCore) {
        if (questionIndex < currentStepIndex) {
          completedCoreCount++
          nonCoreZonePosition = 0
        } else if (questionIndex === currentStepIndex) {
          currentCoreQuestion = question
          foundCurrentCore = true
          questionsSinceCurrentCore = 0
        } else if (!foundNextCore) {
          nextCoreQuestion = question
          foundNextCore = true
          if (!foundCurrentCore) {
            // Current step is non-core: zone ends here, total = all non-core in the zone.
            totalQuestionsUntilNextCore = nonCoreZonePosition
          }
        }
      } else {
        if (foundCurrentCore && !foundNextCore) {
          // Core current step: count non-core questions after it until the next core.
          totalQuestionsUntilNextCore++
        } else if (!foundCurrentCore && !foundNextCore) {
          // Non-core current step: accumulate the zone count both before and after the
          // current step so totalQuestionsUntilNextCore reflects the full zone size when
          // the next core is reached. questionsSinceCurrentCore is captured at the
          // current step's position.
          nonCoreZonePosition++
          if (questionIndex === currentStepIndex) {
            questionsSinceCurrentCore = nonCoreZonePosition
          }
        }
      }
    }

    return {
      completedCoreCount,
      currentCoreQuestion,
      nextCoreQuestion,
      questionsSinceCurrentCore,
      totalQuestionsUntilNextCore,
    }
  }

  /** Returns the active core questions for the given form state. */
  getCoreQuestions(
    questions: QuestionStepWithCoreIf[],
    formResults: FormStepResult,
  ): QuestionStepWithCoreIf[] {
    const evaluator = coreQuestionEvaluator(formResults)
    return questions.filter((question) => evaluator.isCoreQuestion(question.coreIf))
  }

  /** Returns true if the specific question is core for the given form state. */
  isQuestionCore(question: QuestionStepWithCoreIf, formResults: FormStepResult): boolean {
    const evaluator = coreQuestionEvaluator(formResults)
    return evaluator.isCoreQuestion(question.coreIf)
  }
}
